import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Label, Loader, Message, Modal, Table } from 'semantic-ui-react'
import { X } from 'phosphor-react'
import styled from 'styled-components'
import { FlexRowBetween, TitleText } from 'components'
import { useNotification } from 'hooks/toast'
import { MaintenanceService } from 'utility'
import {
    FREQUENCY_UNIT_OPTIONS,
    PRIORITY_OPTIONS,
    buildSchedulePayload,
    emptyScheduleForm,
    formatDate,
    maintenanceApiError,
    scheduleFormFromDto,
} from './maintenance-utils'
import { VerificationSettings } from './VerificationSettings'

const Section = styled.div`
    font-weight: 600;
    margin: 18px 0 8px;
`

const Hint = styled.div`
    color: #6b7280;
    font-size: 12px;
    margin: -6px 0 12px;
`

const OCCURRENCE_COLORS = { Generated: 'green', Failed: 'red', Skipped: 'grey', Pending: 'blue' }

/**
 * Create / edit a maintenance schedule of one asset (EAUDITOR-405). In edit mode it also lists the recent runs
 * (generated / failed / skipped, EAUDITOR-406) with Retry for failed ones.
 * Props: open, assetId, scheduleId (null = new), canEdit, onClose(changed?), templateOptions, userOptions, customerOptions.
 */
export const MaintenanceScheduleModal = ({
    open,
    assetId,
    scheduleId = null,
    canEdit = true,
    onClose,
    templateOptions = [],
    userOptions = [],
    customerOptions = [],
}) => {
    const { successToast, errorToast } = useNotification()
    const [form, setForm] = useState(emptyScheduleForm())
    const [errors, setErrors] = useState({})
    const [serverError, setServerError] = useState('')
    const [saving, setSaving] = useState(false)
    const [loading, setLoading] = useState(false)
    const [types, setTypes] = useState([])
    const [detail, setDetail] = useState(null)
    const [changed, setChanged] = useState(false)
    const submittingRef = useRef(false)
    const currentRef = useRef(scheduleId)
    currentRef.current = scheduleId

    const load = async id => {
        setLoading(true)
        try {
            const result = await MaintenanceService.getSchedule(id)
            if (currentRef.current !== id) return
            setDetail(result)
            setForm(scheduleFormFromDto(result))
        } catch (error) {
            if (currentRef.current !== id) return
            setServerError(maintenanceApiError(error, 'The schedule could not be loaded.'))
        } finally {
            if (currentRef.current === id) setLoading(false)
        }
    }

    useEffect(() => {
        if (!open) return
        setErrors({})
        setServerError('')
        setChanged(false)
        setDetail(null)
        setForm(emptyScheduleForm())
        if (scheduleId) load(scheduleId)
        MaintenanceService.types()
            .then(result => setTypes(Array.isArray(result) ? result : []))
            .catch(() => setTypes([]))
    }, [open, scheduleId])

    const typeOptions = useMemo(
        () => [
            { key: 0, value: '', text: 'Not set' },
            ...types.map(t => ({ key: t.MaintenanceTypeId, value: t.MaintenanceTypeId, text: t.Name })),
        ],
        [types],
    )
    const departmentOptions = useMemo(
        () => [{ key: 0, value: '', text: 'No department' }, ...(customerOptions || [])],
        [customerOptions],
    )

    const set = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }))
    }
    const err = field => (errors[field] ? { content: errors[field] } : undefined)
    const readOnly = !canEdit

    const close = () => !saving && onClose(changed)

    const save = async () => {
        if (submittingRef.current) return
        const { errors: found, payload } = buildSchedulePayload(form, assetId)
        if (Object.values(found).some(Boolean)) {
            setErrors(found)
            return
        }
        submittingRef.current = true
        setSaving(true)
        setServerError('')
        try {
            if (scheduleId) await MaintenanceService.updateSchedule(scheduleId, payload)
            else await MaintenanceService.createSchedule(payload)
            successToast(scheduleId ? 'Schedule saved' : 'Schedule created')
            onClose(true)
        } catch (error) {
            setServerError(maintenanceApiError(error))
        } finally {
            submittingRef.current = false
            setSaving(false)
        }
    }

    const retry = async occurrenceId => {
        setSaving(true)
        try {
            const result = await MaintenanceService.retryOccurrence(occurrenceId)
            if (result?.Generated > 0) successToast('Task created')
            else errorToast((result?.Messages || []).join(' ') || 'The run failed again.')
            setChanged(true)
            await load(scheduleId)
        } catch (error) {
            if (!error?.roleRefusalHandled) errorToast(maintenanceApiError(error))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal open={open} onClose={close} size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>{scheduleId ? 'Maintenance schedule' : 'New maintenance schedule'}</TitleText>
                    <X size={20} className="pointer" onClick={close} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content scrolling>
                {loading && <Loader active inline="centered" />}
                <Form noValidate>
                    <Form.Input
                        label="Name"
                        required
                        maxLength={200}
                        readOnly={readOnly}
                        placeholder="e.g. Monthly preventive maintenance"
                        value={form.Name}
                        onChange={(e, { value }) => set('Name', value)}
                        error={err('Name')}
                    />
                    <Form.Group widths="equal">
                        <Form.Dropdown
                            label="Maintenance type"
                            selection
                            disabled={readOnly}
                            placeholder="Not set"
                            options={typeOptions}
                            value={form.MaintenanceTypeId}
                            onChange={(e, { value }) => set('MaintenanceTypeId', value)}
                        />
                        <Form.Dropdown
                            label="Priority"
                            selection
                            disabled={readOnly}
                            options={PRIORITY_OPTIONS}
                            value={form.PriorityId}
                            onChange={(e, { value }) => set('PriorityId', value)}
                        />
                    </Form.Group>

                    <Section>Frequency</Section>
                    <Form.Group widths="equal">
                        <Form.Input
                            label="Repeat every"
                            type="number"
                            min={1}
                            max={999}
                            readOnly={readOnly}
                            value={form.FrequencyInterval}
                            onChange={(e, { value }) => set('FrequencyInterval', value)}
                            error={err('FrequencyInterval')}
                        />
                        <Form.Dropdown
                            label="Unit"
                            selection
                            disabled={readOnly}
                            options={FREQUENCY_UNIT_OPTIONS}
                            value={form.FrequencyUnit}
                            onChange={(e, { value }) => set('FrequencyUnit', value)}
                            error={err('FrequencyUnit')}
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input
                            label="Start date"
                            required
                            type="date"
                            readOnly={readOnly}
                            value={form.StartDate}
                            onChange={(e, { value }) => set('StartDate', value)}
                            error={err('StartDate')}
                        />
                        <Form.Input
                            label="Next due date"
                            type="date"
                            readOnly={readOnly}
                            value={form.NextDueDate}
                            onChange={(e, { value }) => set('NextDueDate', value)}
                            error={err('NextDueDate')}
                        />
                        <Form.Input
                            label="Create task days before due"
                            type="number"
                            min={0}
                            max={365}
                            readOnly={readOnly}
                            value={form.LeadDays}
                            onChange={(e, { value }) => set('LeadDays', value)}
                            error={err('LeadDays')}
                        />
                    </Form.Group>
                    <Hint>
                        Empty next due date = the first date of the schedule from today. Each next date counts from the
                        planned date (a late task does not move the schedule); monthly and yearly dates keep the start
                        date&apos;s day (31st, or the month&apos;s last day). If dates are already in the past, only the
                        latest one becomes a task.
                    </Hint>

                    <Section>Work</Section>
                    <Form.Dropdown
                        label="Work checklist (inspection template)"
                        required
                        selection
                        search
                        disabled={readOnly}
                        placeholder="Select the checklist the technician fills in"
                        options={templateOptions}
                        value={form.WorkTemplateId}
                        onChange={(e, { value }) => set('WorkTemplateId', value)}
                        error={err('WorkTemplateId')}
                    />
                    <Form.Group widths="equal">
                        <Form.Dropdown
                            label="Assigned to"
                            required
                            selection
                            search
                            disabled={readOnly}
                            options={userOptions}
                            value={form.AssigneeUserId}
                            onChange={(e, { value }) => set('AssigneeUserId', value)}
                            error={err('AssigneeUserId')}
                        />
                        <Form.Dropdown
                            label="Department / customer"
                            selection
                            search
                            disabled={readOnly}
                            placeholder="No department"
                            options={departmentOptions}
                            value={form.CustomerId}
                            onChange={(e, { value }) => set('CustomerId', value)}
                        />
                    </Form.Group>
                    <Form.TextArea
                        label="Instructions"
                        rows={2}
                        maxLength={2000}
                        readOnly={readOnly}
                        value={form.Instructions}
                        onChange={(e, { value }) => set('Instructions', value)}
                        error={err('Instructions')}
                    />

                    <Section>Inspection requirements</Section>
                    <Form.Checkbox
                        label="Pre-inspection before the work"
                        disabled={readOnly}
                        checked={!!form.PreInspectionRequired}
                        onChange={(e, { checked }) => set('PreInspectionRequired', checked)}
                    />
                    {form.PreInspectionRequired && (
                        <Form.Dropdown
                            label="Pre-inspection template"
                            selection
                            search
                            disabled={readOnly}
                            options={templateOptions}
                            value={form.PreInspectionTemplateId}
                            onChange={(e, { value }) => set('PreInspectionTemplateId', value)}
                            error={err('PreInspectionTemplateId')}
                        />
                    )}
                    {form.PreInspectionRequired && (
                        <Hint>The work checklist can only be completed after the pre-inspection.</Hint>
                    )}
                    <Form.Checkbox
                        label="Verification after the work"
                        disabled={readOnly}
                        checked={!!form.VerificationRequired}
                        onChange={(e, { checked }) => set('VerificationRequired', checked)}
                    />
                    {form.VerificationRequired && (
                        <VerificationSettings
                            form={form}
                            set={set}
                            templateOptions={templateOptions}
                            userOptions={userOptions}
                            errors={errors}
                            disabled={readOnly}
                        />
                    )}
                </Form>

                {detail && (
                    <>
                        <Section>
                            Recent runs{' '}
                            <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}>
                                Next task created on {formatDate(detail.NextGenerationDate)} (due {formatDate(detail.NextDueDate)})
                            </span>
                        </Section>
                        {(detail.Occurrences || []).length === 0 ? (
                            <Hint>No tasks created yet.</Hint>
                        ) : (
                            <Table compact celled unstackable>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Due</Table.HeaderCell>
                                        <Table.HeaderCell>Result</Table.HeaderCell>
                                        <Table.HeaderCell>Task / message</Table.HeaderCell>
                                        <Table.HeaderCell />
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {detail.Occurrences.map(o => (
                                        <Table.Row key={o.OccurrenceId}>
                                            <Table.Cell>{formatDate(o.DueDate)}</Table.Cell>
                                            <Table.Cell>
                                                <Label size="mini" color={OCCURRENCE_COLORS[o.StatusName]}>
                                                    {o.StatusName}
                                                </Label>
                                                {o.AttemptCount > 1 && (
                                                    <div style={{ color: '#6b7280', fontSize: 11 }}>{o.AttemptCount} attempts</div>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {o.TaskTitle ? `${o.TaskTitle}${o.TaskStatusName ? ` (${o.TaskStatusName})` : ''}` : o.LastError || '-'}
                                            </Table.Cell>
                                            <Table.Cell collapsing>
                                                {o.CanRetry && (
                                                    <Button size="mini" basic disabled={saving} onClick={() => retry(o.OccurrenceId)}>
                                                        Retry
                                                    </Button>
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        )}
                    </>
                )}
                {serverError && <Message negative content={serverError} />}
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={close} disabled={saving}>
                    {canEdit ? 'Cancel' : 'Close'}
                </Button>
                {canEdit && (
                    <Button type="button" primary onClick={save} loading={saving} disabled={saving || loading}>
                        {scheduleId ? 'Save' : 'Create'}
                    </Button>
                )}
            </Modal.Actions>
        </Modal>
    )
}
